import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Redirect, useLocation, useHistory } from "react-router-dom";
// styles
import "./all-creators.css";

import ModernHeader from "../../../components/layout/Headers/ModernHeader";
import ModernFootermenu from "../../../components/layout/Footer";
import {
  isUserContentCreator,
  isUserLoggedIn,
  formatProfileTag
} from "../../../shared/utils/function";
import { showError } from "../../../shared/utils/swalHandler";
import NoContent from "../../component/NoContent/NoContent";
import CreatorCard from "../../../features/cards/CreatorCard";
import HundredCreator from "../../component/HundredCreator";
import UserInfoBoxCard from "../../../components/loading/userInfoBox";
import SignupCreator from "../../component/SignupCreator";
import CategoryLists from "../../component/CategoryLists";

// Actions
import { getCreatorLists, getCreatorGuestLists } from "../../../shared/actions/creatorActions";
import { ROUTES } from "../../../shared/constants/routes";
import { renderPaginationButtons } from "../../../shared/utils/paginationUtil";
import { resolveVatRate } from "../../../shared/utils/paymentUtil";
import { reportError } from "../../../shared/sentry";

// Constants
const PAGE_SIZE = 9;
const DEFAULT_SORT = "createdAt_desc";

const SORT_OPTIONS = {
  'username_asc': 'Username A-Z',
  'username_desc': 'Username Z-A',
  'createdAt_asc': 'Oldest First',
  'createdAt_desc': 'Newest First',
  'featuredCreator': 'Featured',
  'reset': 'Sort by'
};

const VALID_SORT_BY = [
  'username_asc',
  'username_desc',
  'createdAt_asc',
  'createdAt_desc',
  'featuredCreator',
  'trendingCreator',
  'reset'
];

function parsePositiveInt(value, fallback) {
  const parsed = parseInt(value, 10);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : fallback;
}

function parseAllCreatorsSearch(search) {
  const params = new URLSearchParams(search);
  const category = params.get("category") || "";
  const sortByParam = params.get("sortBy");
  const sortBy =
    sortByParam && VALID_SORT_BY.includes(sortByParam) && sortByParam !== "reset"
      ? sortByParam
      : DEFAULT_SORT;
  const page = parsePositiveInt(params.get("page"), 1);

  return { category, sortBy, page };
}

/** Build /all-creators query string: category, sortBy (if not default), page (if > 1). */
function buildAllCreatorsPath({ category, sortBy, page }) {
  const params = new URLSearchParams();
  if (category) {
    params.set("category", category);
  }
  if (sortBy && sortBy !== "reset" && sortBy !== DEFAULT_SORT) {
    params.set("sortBy", sortBy);
  }
  if (page > 1) {
    params.set("page", String(page));
  }
  const qs = params.toString();
  return qs ? `${ROUTES.ALL_CREATORS}?${qs}` : ROUTES.ALL_CREATORS;
}

const AllCreators = () => {
  const location = useLocation();
  const history = useHistory();
  const isCreator = isUserContentCreator();
  const userLogged = isUserLoggedIn();
  const userInfo = useSelector((state) => state.user.user);
  const [vatPercentage, setVatPercentage] = useState(0);

  const { category: selectedCategory, sortBy, page: currentPage } = useMemo(
    () => parseAllCreatorsSearch(location.search),
    [location.search]
  );

  const [cards, setCards] = useState([]);
  const [totalCreators, setTotalCreators] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortContainerRef = useRef(null);

  const fetchGenerationRef = useRef(0);

  const goToList = useCallback((nextState, { replace = false } = {}) => {
    const path = buildAllCreatorsPath(nextState);
    const currentPath = `${history.location.pathname}${history.location.search}`;
    if (currentPath === path) {
      return false;
    }
    if (replace) {
      history.replace(path);
    } else {
      history.push(path);
    }
    return true;
  }, [history]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    setSortDropdownOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!sortDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (sortContainerRef.current && !sortContainerRef.current.contains(event.target)) {
        setSortDropdownOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSortDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [sortDropdownOpen]);

  useEffect(() => {
    let cancelled = false;
    const fetchVatPercentage = async () => {
      try {
        const vatRate = await resolveVatRate(userInfo);
        if (!cancelled) {
          setVatPercentage(vatRate);
        }
      } catch (error) {
        reportError(error, "AllCreators fetchVatPercentage");
      }
    };
    fetchVatPercentage();
    return () => {
      cancelled = true;
    };
  }, [userInfo]);

  useEffect(() => {
    const generation = ++fetchGenerationRef.current;
    let cancelled = false;

    const isStale = () => cancelled || generation !== fetchGenerationRef.current;

    const fetchCreators = async () => {
      setLoading(true);
      let keepLoading = false;

      try {
        const params = {
          limit: PAGE_SIZE,
          skip: currentPage - 1,
          sortBy
        };

        if (selectedCategory) {
          params.search = selectedCategory;
        }

        const response = userLogged
          ? await getCreatorLists(params)
          : await getCreatorGuestLists(params);

        if (isStale()) {
          return;
        }

        if (!response.success) {
          setCards([]);
          setTotalCreators(0);
          setTotalPages(1);
          showError("Error", response.error || "Failed to fetch creators");
          return;
        }

        const creators = Array.isArray(response.data?.data)
          ? response.data.data.filter(Boolean)
          : [];
        const total = Number(response.data?.recordsTotal) || 0;
        const pagesFromApi = Number(response.data?.totalPages);
        const pages = Number.isInteger(pagesFromApi) && pagesFromApi >= 1
          ? pagesFromApi
          : Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);

        if (currentPage > pages) {
          const didRedirect = goToList(
            { category: selectedCategory, sortBy, page: pages },
            { replace: true }
          );
          if (didRedirect) {
            keepLoading = true;
            return;
          }
        }

        setCards(creators);
        setTotalCreators(total);
        setTotalPages(pages);
      } catch (error) {
        if (isStale()) {
          return;
        }
        reportError(error, "AllCreators fetchCreators");
        setCards([]);
        setTotalCreators(0);
        setTotalPages(1);
        showError("Error", "Something went wrong. Please try again.");
      } finally {
        if (!isStale() && !keepLoading) {
          setLoading(false);
        }
      }
    };

    fetchCreators();

    return () => {
      cancelled = true;
    };
  }, [currentPage, selectedCategory, sortBy, goToList, userLogged]);

  const handlePageChange = useCallback((page) => {
    const nextPage = parsePositiveInt(page, null);
    if (nextPage == null || nextPage === currentPage || nextPage > totalPages) {
      return;
    }
    // Replace so pagination does not stack history entries; header back can
    // then leave this list without walking through every page.
    goToList(
      { category: selectedCategory, sortBy, page: nextPage },
      { replace: true }
    );
  }, [currentPage, totalPages, selectedCategory, sortBy, goToList]);

  const toggleSortDropdown = useCallback(() => {
    setSortDropdownOpen((prev) => !prev);
  }, []);

  const handleSortChange = useCallback((newSortBy) => {
    const resolved = newSortBy === "reset" ? DEFAULT_SORT : newSortBy;
    if (!VALID_SORT_BY.includes(resolved) || resolved === sortBy) {
      setSortDropdownOpen(false);
      return;
    }
    setSortDropdownOpen(false);
    goToList(
      { category: selectedCategory, sortBy: resolved, page: 1 },
      { replace: true }
    );
  }, [selectedCategory, sortBy, goToList]);

  const handleHeaderBack = useCallback(() => {
    if (selectedCategory) {
      goToList({ category: "", sortBy, page: 1 }, { replace: true });
      return;
    }
    // Stay inside the app: Discover is the parent of this list.
    // replace avoids stacking /discover on top of /all-creators.
    history.replace(ROUTES.DISCOVER);
  }, [selectedCategory, sortBy, goToList, history]);

  const getSortDisplayText = (sortByParam) => {
    return SORT_OPTIONS[sortByParam] || 'Sort by';
  };

  const updateCreatorFollowState = useCallback((creatorId, isFollowing) => {
    if (!creatorId) {
      return;
    }
    setCards(prevCards =>
      prevCards.map(card =>
        card && card._id === creatorId ? { ...card, isFollowing } : card
      )
    );
  }, []);

  if (isCreator) {
    return <Redirect to={ROUTES.MY_PROFILE} />;
  }

  return (
    <div className="all-creators-page">
      {/* Modern Header */}
      <ModernHeader
        showBackButton={true}
        showButtonBorder={false}
        showAvatar={false}
        isBorder={true}
        hideSearchIcon={true}
        onBackClick={handleHeaderBack}
      />

      {/* Main Content */}
      <main className="all-creators-main">
        {/* Filters Section */}
        <div className="all-creators-filters">
          <div className="filters-header">
            <div>
              <h2 className="page-title">
                {selectedCategory ? formatProfileTag(selectedCategory) : 'All Creators'}
              </h2>
              {!selectedCategory ? <p className="page-subtitle">Discover amazing content creators</p> : null}
            </div>

            <div className="sort-container" ref={sortContainerRef}>
              <button
                type="button"
                className="sort-btn"
                onClick={toggleSortDropdown}
                aria-expanded={sortDropdownOpen}
                aria-haspopup="listbox"
              >
                <span>{getSortDisplayText(sortBy)}</span>
                <i className={`fa-solid fa-chevron-${sortDropdownOpen ? 'up' : 'down'}`}></i>
              </button>
              {sortDropdownOpen && (
                <div className="sort-dropdown">
                  <div className="sort-options">
                    <button
                      type="button"
                      className="sort-option reset-option"
                      onClick={() => handleSortChange('reset')}
                    >
                      <i className="fa-solid fa-rotate-left"></i>
                      Reset
                    </button>
                    <div className="sort-divider"></div>
                    <button
                      type="button"
                      className={`sort-option ${sortBy === 'createdAt_desc' ? 'active' : ''}`}
                      onClick={() => handleSortChange('createdAt_desc')}
                    >
                      Newest First
                      {sortBy === 'createdAt_desc' && <i className="fa-solid fa-check"></i>}
                    </button>
                    <button
                      type="button"
                      className={`sort-option ${sortBy === 'username_asc' ? 'active' : ''}`}
                      onClick={() => handleSortChange('username_asc')}
                    >
                      Username A-Z
                      {sortBy === 'username_asc' && <i className="fa-solid fa-check"></i>}
                    </button>
                    <button
                      type="button"
                      className={`sort-option ${sortBy === 'username_desc' ? 'active' : ''}`}
                      onClick={() => handleSortChange('username_desc')}
                    >
                      Username Z-A
                      {sortBy === 'username_desc' && <i className="fa-solid fa-check"></i>}
                    </button>
                    <button
                      type="button"
                      className={`sort-option ${sortBy === 'createdAt_asc' ? 'active' : ''}`}
                      onClick={() => handleSortChange('createdAt_asc')}
                    >
                      Oldest First
                      {sortBy === 'createdAt_asc' && <i className="fa-solid fa-check"></i>}
                    </button>
                    <button
                      type="button"
                      className={`sort-option ${sortBy === 'featuredCreator' ? 'active' : ''}`}
                      onClick={() => handleSortChange('featuredCreator')}
                    >
                      Featured
                      {sortBy === 'featuredCreator' && <i className="fa-solid fa-check"></i>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Connect with Creators CTA */}
        {selectedCategory || totalCreators < 100 ? null : (
          <HundredCreator
            lists={
              cards.length > 0
                ? cards.filter((creator) => creator && creator.profile_picture).slice(0, 3)
                : []
            }
            totalCreators={totalCreators}
          />
        )}

        {/* Creators Grid with Skeleton Loading */}
        <div className="creators-content-wrapper">
          {loading ? (
            <div className="all-creators-grid skeleton-loading">
              <UserInfoBoxCard count={PAGE_SIZE} />
            </div>
          ) : cards.length > 0 ? (
            <div className="all-creators-grid content-loaded">
              {cards.filter(Boolean).map((creator, i) => (
                <CreatorCard
                  key={creator._id || creator.id || creator.username || `creator-${i}`}
                  index={i}
                  creator={creator}
                  vatPercentage={vatPercentage}
                  isShowSubscriptionPrice={false}
                  isAllowFollowOrUnfollow={true}
                  onFollowSuccess={() => updateCreatorFollowState(creator._id, true)}
                  onUnfollowSuccess={() => updateCreatorFollowState(creator._id, false)}
                  tab="all-creators"
                />
              ))}
            </div>
          ) : (
            <NoContent />
          )}
        </div>

        {/* Pagination - Always visible when there are multiple pages */}
        {totalPages > 1 && (
          <div className="all-creators-pagination">
            {renderPaginationButtons(currentPage, totalPages, handlePageChange)}
          </div>
        )}

        {/* Categories Section */}
        <CategoryLists />

        {/* CTA Section - Only for guest users */}
        {!userLogged && <SignupCreator />}
      </main>

      {/* Footer */}
      <ModernFootermenu />
    </div>
  );
};

export default AllCreators;
